import { useMemo, useState } from 'react';
import { Skeleton, Progress, SegmentedControl, Divider, type ProgressTone } from '@vnetwork/ui/primitives';
import { Stat, StatGroup, BarList } from '@vnetwork/ui/data';
import { useRevenueDashboardRange, type RevenueMonthly } from '@/api/dashboard-feeds';
import { formatBillion } from '@/lib/format';
import { WidgetCard } from './WidgetCard';

/** Preset month spans (inclusive of the current month). */
const PRESETS = [
  { value: '3', label: '3M', months: 3 },
  { value: '6', label: '6M', months: 6 },
  { value: '12', label: '12M', months: 12 },
];

/** Prev-N-months "YYYY-MM" range (inclusive of current month) + a label. */
function monthRange(months: number): { gte: string; lte: string; label: string } {
  const now = new Date();
  const lte = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const start = new Date(now.getFullYear(), now.getMonth() - (months - 1), 1);
  const gte = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`;
  const fmt = (d: Date) => d.toLocaleString('en', { month: 'short', year: 'numeric' });
  return { gte, lte, label: `${fmt(start)} – ${fmt(now)}` };
}

/** Completion → progress tone (behind = danger, on-track = warning, met = success). */
function toneForCompletion(pct: number): ProgressTone {
  if (pct >= 100) return 'success';
  if (pct >= 70) return 'warning';
  return 'danger';
}

/** Short "Q1"/"H1" or index label for a monthly_comparison entry. */
function periodLabel(m: RevenueMonthly, i: number): string {
  return m.quarter || m.half_year || `P${i + 1}`;
}

/**
 * Revenue statistics — Actual / Target / Completion stats, a target progress
 * bar, a month-range preset filter, and a per-period actual-vs-target
 * breakdown. Mirrors the legacy Dashboard `TargetRevenue` panel. Reads
 * `/dashboard?gte=&lte=` (summary + monthly_comparison). Amounts are VND.
 */
export function RevenueStatsWidget() {
  const [preset, setPreset] = useState('3');
  const months = Number(preset);
  const { gte, lte, label } = useMemo(() => monthRange(months), [months]);
  const { data, isLoading } = useRevenueDashboardRange(gte, lte);

  const actual = data?.summary.total_actual_amount ?? 0;
  const target = data?.summary.total_target_amount ?? 0;
  const pct = target > 0 ? Math.round((actual / target) * 100) : 0;

  const bars = useMemo(() => {
    const rows = data?.monthly ?? [];
    return rows.flatMap((m, i) => {
      const a = Number(m.period_actual) || 0;
      const t = Number(m.period_target) || 0;
      return [
        { label: `${periodLabel(m, i)} · Actual`, value: a, display: formatBillion(a), color: 'var(--vn-success-solid)' },
        { label: `${periodLabel(m, i)} · Target`, value: t, display: formatBillion(t), color: 'var(--vn-border-strong)' },
      ];
    });
  }, [data]);

  return (
    <WidgetCard
      title="Revenue statistics"
      description={label}
      actions={
        <SegmentedControl
          size="sm"
          aria-label="Revenue period"
          items={PRESETS.map((p) => ({ value: p.value, label: p.label }))}
          value={preset}
          onValueChange={setPreset}
        />
      }
    >
      {isLoading ? (
        <Skeleton style={{ height: 120 }} />
      ) : (
        <>
          <StatGroup columns={3}>
            <Stat label="Accumulated" value={formatBillion(actual)} dotColor="var(--vn-success-solid)" />
            <Stat label="Target" value={target > 0 ? formatBillion(target) : '—'} dotColor="var(--vn-border-strong)" />
            <Stat label="Completion" value={`${pct}%`} />
          </StatGroup>

          <div style={{ marginTop: 'var(--vn-space-4)' }}>
            <Progress
              value={pct}
              tone={toneForCompletion(pct)}
              size="lg"
              label={`Accumulated ${formatBillion(actual)}`}
              showValue
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 'var(--vn-space-1)',
                fontSize: 'var(--vn-font-size-xs)',
                color: 'var(--vn-fg-muted)',
              }}
            >
              <span>0</span>
              <span>Target: {target > 0 ? formatBillion(target) : '—'}</span>
            </div>
          </div>

          {bars.length > 0 ? (
            <>
              <Divider style={{ margin: 'var(--vn-space-4) 0' }} />
              <div
                style={{
                  fontSize: 'var(--vn-font-size-xs)',
                  fontWeight: 'var(--vn-font-weight-medium)',
                  color: 'var(--vn-fg-muted)',
                  marginBottom: 'var(--vn-space-2)',
                }}
              >
                By period
              </div>
              <BarList items={bars} />
            </>
          ) : null}
        </>
      )}
    </WidgetCard>
  );
}
